/**
 * @fileoverview Canonicalize URLs before they are sent to GA4.
 * @description Combines pathname junk stripping (crawler-appended punctuation) with
 * query repair/noise removal so page_path and page_location consolidate correctly.
 *
 * @module lib/analytics/normalize-analytics-url
 */

import { stripTrailingPathJunk } from '~/lib/redirects/trailingJunk'

import {
  normalizeQuerySearch,
  repairMalformedSearchString,
  stripAnalyticsNoiseQueryParams,
} from '~/lib/analytics/normalize-query-string'

/**
 * Normalizes a URL string for analytics reporting.
 *
 * @param {string} input - Relative (`/path?x=1`) or absolute href.
 * @param {string} [baseOrigin='https://www.adsights.ai'] - Origin for relative URLs.
 * @returns {string} Normalized URL in the same shape (relative vs absolute) as input.
 */
export function normalizeAnalyticsUrl(
  input: string,
  baseOrigin = 'https://www.adsights.ai',
): string {
  try {
    const isAbsolute = /^[a-z]+:\/\//i.test(input)
    const u = new URL(input, baseOrigin)

    const dejunkedPath = stripTrailingPathJunk(u.pathname)
    if (dejunkedPath) {
      u.pathname = dejunkedPath
    }

    const repaired = repairMalformedSearchString(u.search)
    u.search = stripAnalyticsNoiseQueryParams(repaired)

    const normalized = isAbsolute ? u.href : `${u.pathname}${u.search}${u.hash}`
    return normalized === input ? input : normalized
  } catch {
    return input
  }
}

/**
 * Returns whether a href contains analytics-hygiene issues (malformed query or path junk).
 *
 * @param {string} href - Internal path or URL from authored content.
 * @returns {boolean} True when repair would change the href.
 */
export function hrefNeedsAnalyticsHygieneRepair(href: string): boolean {
  try {
    const pathPart = href.split('#')[0]?.split('?')[0] ?? href
    if (stripTrailingPathJunk(pathPart)) return true
    const search = href.includes('?') ? `?${href.split('?').slice(1).join('?')}` : ''
    return normalizeQuerySearch(search) !== null
  } catch {
    return false
  }
}
